import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, MFASetupData } from '../../context/AuthContext';
import Card from '../common/Card';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { CheckCircleIcon, QrCodeIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

export const MFAEnrollment: React.FC = () => {
  const [mfaSetupData, setMfaSetupData] = useState<MFASetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentComplete, setEnrollmentComplete] = useState(false);
  const { generateMFASecret, enrollMFA, error, clearError } = useAuth();

  const generateSecret = useCallback(async () => {
    try {
      setIsGenerating(true);
      clearError();
      const setupData = await generateMFASecret();
      setMfaSetupData(setupData);
    } catch (error) {
      console.error('Failed to generate MFA secret:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [clearError, generateMFASecret]);

  useEffect(() => {
    generateSecret();
  }, [generateSecret]);

  const handleEnrollMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!mfaSetupData || !verificationCode.trim()) {
      return;
    }

    try {
      setIsEnrolling(true);
      clearError();
      console.log('Starting MFA enrollment...');
      await enrollMFA(mfaSetupData.secret, verificationCode);
      console.log('MFA enrollment completed successfully');
      
      // Set enrollmentComplete to show success message since state transition isn't working
      // This provides better user feedback in the emulator environment
      console.log('MFA enrollment state updated, showing success screen');
      setEnrollmentComplete(true);
    } catch (error) {
      console.error('Failed to enroll MFA:', error);
    } finally {
      setIsEnrolling(false);
    }
  };

  if (enrollmentComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              MFA Setup Complete!
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Your account is now protected with two-factor authentication
            </p>
          </div>

          <Card className="mt-8 space-y-6">
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center">
                <CheckCircleIcon className="h-12 w-12 text-green-500" />
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Two-Factor Authentication Enabled
                </h3>
                
                <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
                  <p className="text-sm text-green-800">
                    <strong>Success!</strong> Two-factor authentication has been set up for your account.
                  </p>
                  <div className="text-sm text-green-700 mt-2">
                    <p>To complete the setup, please sign in again with:</p>
                    <ol className="list-decimal list-inside mt-1 space-y-1">
                      <li>Your email and password</li>
                      <li>A 6-digit code from your authenticator app</li>
                    </ol>
                  </div>
                </div>

                {process.env.REACT_APP_ENVIRONMENT === 'development' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Development Mode:</strong> Since you're using the emulator, you can use any 6-digit code (like 123456) when prompted for MFA during sign-in.
                    </p>
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  console.log('MFA setup complete - user needs to sign in again with MFA');
                  // In emulator mode, user needs to sign in again to complete the flow
                  window.location.reload();
                }}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Sign In with Two-Factor Authentication
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Set Up Two-Factor Authentication
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Secure your account with an authenticator app
          </p>
        </div>

        <Card className="mt-8 space-y-6">
          <div>
            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-4">
                <div className="text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            )}

            {isGenerating ? (
              <div className="text-center py-8">
                <LoadingSpinner size="large" />
                <p className="text-sm text-gray-600 mt-4">Generating setup code...</p>
              </div>
            ) : mfaSetupData ? (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-5 w-5 text-blue-500 mr-2" />
                    <h3 className="text-sm font-medium text-blue-800">
                      Required Security Step
                    </h3>
                  </div>
                  <p className="text-sm text-blue-700 mt-2">
                    Two-factor authentication is mandatory for all FinStack accounts to ensure maximum security.
                  </p>
                  {process.env.REACT_APP_ENVIRONMENT === 'development' && (
                    <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                      <strong>Development Mode:</strong> Using emulator simulation. Enter any 6-digit code (e.g., 123456) to proceed.
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Step 1: Scan QR Code
                  </h3>
                  
                  <div className="text-center">
                    <div className="inline-block p-4 bg-white border rounded-lg shadow-sm">
                      {mfaSetupData.qrCodeUrl ? (
                        <img 
                          src={mfaSetupData.qrCodeUrl} 
                          alt="MFA QR Code"
                          className="w-48 h-48"
                        />
                      ) : (
                        <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded">
                          <QrCodeIcon className="h-16 w-16 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-4">
                      Scan this QR code with your authenticator app
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Recommended Authenticator Apps:
                    </h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Google Authenticator</li>
                      <li>• Microsoft Authenticator</li>
                      <li>• Authy</li>
                      <li>• 1Password</li>
                    </ul>
                  </div>
                </div>

                <form onSubmit={handleEnrollMFA} className="space-y-4">
                  <div>
                    <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700">
                      Step 2: Enter 6-digit code from your authenticator app
                    </label>
                    <input
                      id="verification-code"
                      name="verificationCode"
                      type="text"
                      maxLength={6}
                      pattern="[0-9]{6}"
                      autoComplete="one-time-code"
                      required
                      className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-center font-mono"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={isEnrolling}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isEnrolling || verificationCode.length !== 6}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isEnrolling ? (
                      <LoadingSpinner size="small" color="white" />
                    ) : (
                      'Enable Two-Factor Authentication'
                    )}
                  </button>
                </form>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-red-600">Failed to generate setup code. Please try again.</p>
                <button
                  onClick={generateSecret}
                  className="mt-4 text-sm text-blue-600 hover:text-blue-500"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};